import React, { useEffect, Component, ErrorInfo, ReactNode } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
// @ts-ignore
import Lenis from 'lenis';

import HomePage from './components/pages/HomePage';
import BlogLayout from './components/layout/BlogLayout';
import BlogPageContent from './components/pages/BlogPageContent';
import BlogPostContent from './components/pages/BlogPostContent';
import PrivacyPolicyPage from './components/pages/PrivacyPolicyPage';
import TermsOfServicePage from './components/pages/TermsOfServicePage';
import FloatingContactWidget from './components/ui/FloatingContactWidget';
import { ContactPage } from './components/pages/SectionPages';
import { LocationPage } from './components/pages/LocationPage';
import NotFoundPage from './components/pages/NotFoundPage';
import { ServiceFormProvider } from './contexts/ServiceFormContext';
import ServiceFormPopup from './components/ui/ServiceFormPopup';
import { useServiceForm } from './contexts/ServiceFormContext';
import { ContactFormProvider } from './contexts/ContactFormContext';
import ContactFormPopup from './components/ui/ContactFormPopup';
import { useContactForm } from './contexts/ContactFormContext';
import HashScroller from './components/utils/HashScroller';
import { updateMeta } from './utils/meta';
import { getBlogPost } from './lib/blogUtils';

// Error Boundary Component
interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<{ children: ReactNode }, ErrorBoundaryState> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background">
          <div className="text-center p-8">
            <h2 className="text-2xl font-bold mb-4">Oops! Something went wrong</h2>
            <p className="text-muted-foreground mb-4">
              {this.state.error?.message || 'An unexpected error occurred'}
            </p>
            <button
              onClick={() => {
                this.setState({ hasError: false, error: null });
                window.location.reload();
              }}
              className="px-6 py-2 bg-primary text-primary-foreground rounded-md hover:opacity-90 transition-opacity"
            >
              Try again
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Component to track route changes
const RouteListener: React.FC<{ onPathChange: (path: string) => void; children: React.ReactNode }> = ({ onPathChange, children }) => {
  const location = useLocation();
  
  useEffect(() => {
    onPathChange(location.pathname + location.hash);
  }, [location, onPathChange]);
  
  return <>{children}</>;
};

// Component to render the service form popup
const ServiceFormPopupWrapper: React.FC = () => {
  const { isOpen, selectedService, scrollPosition, closeForm } = useServiceForm();
  
  return (
    <ServiceFormPopup 
      isOpen={isOpen}
      onClose={closeForm}
      preSelectedService={selectedService}
      initialScrollPosition={scrollPosition}
    />
  );
};

// Component to render the contact form popup
const ContactFormPopupWrapper: React.FC = () => {
  const { isOpen, scrollPosition, closeForm, title } = useContactForm();
  
  return (
    <ContactFormPopup 
      isOpen={isOpen}
      onClose={closeForm}
      title={title}
      initialScrollPosition={scrollPosition}
    />
  );
};

// Component to redirect to static QR landing page
const QRRedirect: React.FC = () => {
  useEffect(() => {
    window.location.replace('/qr/');
  }, []);
  return null;
};

const App: React.FC = () => {
  const [componentsLoaded, setComponentsLoaded] = React.useState(false);
  const [currentPath, setCurrentPath] = React.useState('/');
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  
  // Step 1: Wait for component mount and next frame
  useEffect(() => {
    // Use RAF to ensure we're after the initial render
    const rafId = requestAnimationFrame(() => {
      setComponentsLoaded(true);
    });
    
    return () => cancelAnimationFrame(rafId);
  }, []);
  
  // Step 2: After components are loaded, reveal them
  useEffect(() => {
    if (!componentsLoaded) return;
    
    // Small delay to ensure all CSS transitions are ready
    const timer = setTimeout(() => {
      document.documentElement.classList.add('sections-loaded');
      document.documentElement.classList.add('sections-ready');
    }, 50);
    
    return () => clearTimeout(timer);
  }, [componentsLoaded]);
  
  // Initialize smooth scrolling
  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      touchMultiplier: 2,
    });
    function raf(time: number) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    return () => {
      lenis.destroy();
    };
  }, []);

  // Update meta by route
  useEffect(() => {
    const path = currentPath;
    // Defaults for homepage
    let metaTitle = 'Paokhum - Websites & CCTV Solutions';
    let metaDesc = 'Professional web development and CCTV integration solutions. Modern, secure, and affordable.';
    let image = '/images/og/home.svg';

    // Pricing hash preview
    if (path.includes('#pricing')) {
      metaTitle = 'Pricing - Paokhum';
      image = '/images/og/pricing.svg';
    }

    // Blog listing and posts
    if (path.startsWith('/blog')) {
      metaTitle = 'Blog - Paokhum';
      image = '/images/og/blog.svg';

      // If it's a specific blog post: /blog/:category/:slug
      const m = path.match(/^\/blog\/([^\/]+)\/([^\/?#]+)/);
      if (m) {
        const [, category, slug] = m;
        const post = getBlogPost(category, slug);
        if (post) {
          metaTitle = `${post.title} - Paokhum`;
          if (post.excerpt) metaDesc = post.excerpt;
          if (post.image) image = post.image; // use post banner if provided
        }
      }
    }

    updateMeta({
      title: metaTitle,
      description: metaDesc,
      url: origin + path,
      image,
    });
  }, [currentPath, origin]);

  // Initialize intersection observer for animations
  useEffect(() => {
    // Small delay to ensure DOM is ready after route change
    const timer = setTimeout(() => {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const element = entry.target as HTMLElement;
              const delay = element.dataset.delay || '0s';
              element.style.transitionDelay = delay;
              element.classList.add('is-visible');
              observer.unobserve(element);
            }
          });
        },
        {
          threshold: 0.1,
          rootMargin: '50px',
        }
      );

      // Reset and observe all elements with animation class
      const elements = document.querySelectorAll('.animate-on-scroll');
      elements.forEach((el) => {
        const htmlEl = el as HTMLElement;
        // Skip reset for intro section on mobile to prevent flash
        const isIntroSection = htmlEl.closest('#intro');
        const isMobile = window.innerWidth < 768;
        
        if (isIntroSection && isMobile) {
          // Just mark as visible without animation on mobile
          htmlEl.classList.add('is-visible');
        } else {
          // Reset the animation state for other elements
          htmlEl.classList.remove('is-visible');
          htmlEl.style.transitionDelay = '0s';
          // Re-observe
          observer.observe(el);
        }
      });

      return () => observer.disconnect();
    }, 100);

    return () => clearTimeout(timer);
  }, [currentPath]); // Re-run when path changes

  // Cursor-following glow effect — cached DOM refs for performance
  useEffect(() => {
    let animationFrame: number | null = null;
    let lastUpdate = 0;
    const throttleMs = 16; // ~60fps
    
    // Cache element references instead of querySelectorAll on every mousemove
    let cachedOverlays: HTMLElement[] = [];
    let cachedCards: HTMLElement[] = [];
    
    const refreshCache = () => {
      cachedOverlays = Array.from(document.querySelectorAll<HTMLElement>('.hero-grid'));
      cachedCards = Array.from(document.querySelectorAll<HTMLElement>('.glow-card, .service-card'));
    };
    
    // Initial cache + refresh on DOM mutations (e.g. route changes)
    refreshCache();
    const cacheTimer = setInterval(refreshCache, 2000);
    
    const updateGlow = (e: MouseEvent) => {
      const now = performance.now();
      if (now - lastUpdate < throttleMs) return;
      
      if (animationFrame) cancelAnimationFrame(animationFrame);
      lastUpdate = now;
      
      animationFrame = requestAnimationFrame(() => {
        // Use cached references — no DOM query per frame
        for (const el of cachedOverlays) {
          const rect = el.getBoundingClientRect();
          el.style.setProperty('--glow-x', `${e.clientX - rect.left}px`);
          el.style.setProperty('--glow-y', `${e.clientY - rect.top}px`);
        }
        
        for (const el of cachedCards) {
          const rect = el.getBoundingClientRect();
          el.style.setProperty('--card-x', `${e.clientX - rect.left}px`);
          el.style.setProperty('--card-y', `${e.clientY - rect.top}px`);
        }
      });
    };
    
    window.addEventListener('mousemove', updateGlow);
    
    return () => {
      window.removeEventListener('mousemove', updateGlow);
      clearInterval(cacheTimer);
      if (animationFrame) cancelAnimationFrame(animationFrame);
    };
  }, []);


  // Hide grid overlays completely on blog pages
  const isBlogPage = currentPath.startsWith('/blog');

  return (
    <ErrorBoundary>
      <ServiceFormProvider>
        <ContactFormProvider>
          <Router>
          <div className="relative text-foreground font-sans antialiased min-h-screen bg-background">
          
          {/* Global noise overlay */}
          <div className="noise-overlay" aria-hidden="true" />
          
          {/* Fixed background layer for IntroSection - behind grid */}
          {!isBlogPage && (
            <div 
              className="fixed inset-0 bg-accent pointer-events-none"
              style={{ 
                zIndex: 0,
                maskImage: 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0) 100vh, rgba(0,0,0,1) 100vh, rgba(0,0,0,1) 200vh, rgba(0,0,0,0) 200vh)',
                WebkitMaskImage: 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0) 100vh, rgba(0,0,0,1) 100vh, rgba(0,0,0,1) 200vh, rgba(0,0,0,0) 200vh)'
              }}
              aria-hidden="true"
            />
          )}
          
          {/* Global fixed grid overlay - hidden on blog pages (footer has its own) */}
          {!isBlogPage && (
            <div 
              className="hero-grid"
              aria-hidden="true" 
              style={{ zIndex: 1 }} 
            />
          )}
          
          {/* Main content wrapper - no z-index to avoid new stacking context */}
          <div>
            <RouteListener onPathChange={setCurrentPath}>
            <HashScroller />
            <Routes>
              <Route path="/" element={<HomePage />} />

              {/* Contact page route */}
              <Route path="/contact" element={<ContactPage />} />

              {/* Legal */}
              <Route path="/privacy" element={<PrivacyPolicyPage />} />
              <Route path="/terms" element={<TermsOfServicePage />} />
              
              {/* Blog routes with shared layout */}
              <Route path="/blog" element={<BlogLayout />}>
                <Route index element={<BlogPageContent />} />
                <Route path=":category/:slug" element={<BlogPostContent />} />
              </Route>

              {/* Locations */}
              <Route path="/locations/imphal" element={<LocationPage slug="imphal" />} />
              <Route path="/locations/guwahati" element={<LocationPage slug="guwahati" />} />
              <Route path="/locations/nagaland" element={<LocationPage slug="nagaland" />} />
              <Route path="/locations/northeast" element={<LocationPage slug="northeast" />} />

              {/* QR Landing Page */}
              <Route path="/qr" element={<QRRedirect />} />
              <Route path="/qr/*" element={<QRRedirect />} />

              {/* 404 */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
            </RouteListener>
            
            {/* Global Service Form Popup */}
            <ServiceFormPopupWrapper />
            
            {/* Global Contact Form Popup */}
            <ContactFormPopupWrapper />
            
            {/* Global Floating Contact Widget */}
            <FloatingContactWidget />
          </div>
          </div>
          </Router>
        </ContactFormProvider>
      </ServiceFormProvider>
    </ErrorBoundary>
  );
};

export default App;
